import { type FC, type ReactNode, useCallback } from "react";

import {
  Box,
  Text,
  Button,
  Row,
  Tooltip,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  IconButton,
} from "@hightouch/htui";
import { range } from "lodash";

export type PaginationProps = {
  setPage: (page: number) => void;
  page: number;
  text?: string;
  label?: string;
  count: number | undefined;
  rowsPerPage: number;
  disabledTooltip?: string;
  compact?: boolean;
  alwaysShow?: boolean;
};

export const Pagination: FC<Readonly<PaginationProps>> = ({
  setPage,
  disabledTooltip,
  page,
  compact = false,
  label = "rows",
  count,
  rowsPerPage,
  alwaysShow = false,
  ...props
}) => {
  const text = props.text ?? `${count ?? 0} ${label}`;
  const pages = Math.ceil((count ?? 0) / rowsPerPage);

  if (pages <= 1 && !alwaysShow) {
    return null;
  }

  const onSetPage = (newPage: number) => {
    if (newPage > pages) {
      setPage(pages);
    } else if (newPage < 0) {
      setPage(0);
    } else {
      setPage(newPage);
    }
  };

  // TODO: Fix these hook issues.
  // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  const next = useCallback(
    (n = 1) => {
      onSetPage(page + n);
    },
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    [onSetPage, page],
  );

  // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  const prev = useCallback(
    (n = 1) => {
      onSetPage(page - n);
    },
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    [onSetPage, page],
  );

  let elements: ReactNode[] = [];

  if (pages <= 5) {
    elements = range(0, pages).map((index) => (
      <Page
        key={index}
        active={page === index}
        page={index}
        onClick={() => onSetPage(index)}
      />
    ));
  } else {
    if (page < 4) {
      elements = [
        range(0, 5).map((index) => (
          <Page
            key={index}
            active={page === index}
            page={index}
            onClick={() => onSetPage(index)}
          />
        )),
        <Ellipsis key="ellipsis-5" forward onClick={() => next(5)} />,
        <Page
          key={pages - 1}
          page={pages - 1}
          onClick={() => onSetPage(pages - 1)}
        />,
      ];
    } else if (page >= 4 && page < pages - 4) {
      elements = [
        <Page key={0} page={0} onClick={() => onSetPage(0)} />,
        <Ellipsis key="ellipsis-start" backward onClick={() => prev(5)} />,
        <Page
          key={page - 1}
          page={page - 1}
          onClick={() => onSetPage(page - 1)}
        />,
        <Page key={page} active page={page} onClick={() => onSetPage(page)} />,
        <Page
          key={page + 1}
          page={page + 1}
          onClick={() => onSetPage(page + 1)}
        />,
        <Ellipsis key="ellipsis-end" forward onClick={() => next(5)} />,
        <Page
          key={pages - 1}
          page={pages - 1}
          onClick={() => onSetPage(pages - 1)}
        />,
      ];
    } else {
      elements = [
        <Page key={0} page={0} onClick={() => onSetPage(0)} />,
        <Ellipsis key="ellipsis-1" backward onClick={() => prev(5)} />,
        ...range(pages - 5, pages).map((index) => (
          <Page
            key={index}
            active={page === index}
            page={index}
            onClick={() => onSetPage(index)}
          />
        )),
      ];
    }
  }

  if (compact) {
    return (
      <Row gap={2} align="center">
        <IconButton
          variant="secondary"
          aria-label="Previous page"
          icon={ChevronLeftIcon}
          isDisabled={page === 0}
          size="sm"
          onClick={() => prev()}
        />
        <Text color="text.secondary">
          {page + 1} of {pages}
        </Text>
        <Tooltip
          isDisabled={page === pages - 1 || !disabledTooltip}
          message={disabledTooltip || ""}
        >
          <IconButton
            variant="secondary"
            aria-label="Next page"
            icon={ChevronRightIcon}
            isDisabled={page === pages - 1}
            size="sm"
            onClick={() => next()}
          />
        </Tooltip>
      </Row>
    );
  }

  return (
    <Row gap={1} align="center">
      {text && (
        <Text color="text.secondary" mr={3}>
          {text}
        </Text>
      )}
      <Button isDisabled={page === 0} size="sm" onClick={() => prev()}>
        Previous
      </Button>
      {elements}
      <Tooltip
        isDisabled={page === pages - 1 || !disabledTooltip}
        message={disabledTooltip || ""}
      >
        <Button
          isDisabled={page === pages - 1}
          size="sm"
          onClick={() => next()}
        >
          Next
        </Button>
      </Tooltip>
    </Row>
  );
};

const Page = ({ active = false, page, onClick }) => {
  return (
    <Button size="sm" isDisabled={active} onClick={onClick}>
      {page + 1}
    </Button>
  );
};

const Ellipsis = ({ onClick, forward = false, backward = false }) => {
  return (
    <Box
      sx={{
        svg: { display: "none", width: "14px" },
        ":hover": { span: { display: "none" }, svg: { display: "block" } },
      }}
    >
      <Button size="sm" variant="secondary" onClick={onClick}>
        {backward && <Box as={ChevronDoubleLeftIcon} fontSize="14px" />}
        <Box as="span" width="14px">
          …
        </Box>
        {forward && <Box as={ChevronDoubleRightIcon} fontSize="14px" />}
      </Button>
    </Box>
  );
};

export const truncateData = <T,>(
  data: T[],
  limit: number,
  offset: number,
): T[] => {
  return data.slice(offset, offset + limit);
};
